import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Avatar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday,
  Person,
  CheckCircle,
  Phone,
  Email,
  LocationOn,
  Schedule,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { publicBookingApi } from '../../api/publicBookingApi';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  doctorId: string;
}

interface BookingFormData {
  // Patient Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  age: string;
  gender: string;
  
  // Appointment Details
  doctorId: string;
  departmentId: string;
  locationId: string;
  appointmentDate: Date | null;
  selectedSlot: string; // slot ID
  reason: string;
  notes: string;
}

interface ValidationErrors {
  [key: string]: string;
}

const steps = ['Patient Details', 'Appointment Details', 'Confirmation'];

const BookAppointment: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState<BookingFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    gender: '',
    doctorId: '',
    departmentId: '',
    locationId: '',
    appointmentDate: new Date(), // Default to today
    selectedSlot: '',
    reason: '',
    notes: '',
  });

  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Data for dropdowns
  const [doctors, setDoctors] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load available slots when doctor or date changes
  useEffect(() => {
    if (formData.doctorId && formData.appointmentDate) {
      loadAvailableSlots();
    } else {
      setAvailableSlots([]);
      setFormData(prev => ({ ...prev, selectedSlot: '' }));
    }
  }, [formData.doctorId, formData.appointmentDate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [doctorsRes, locationsRes] = await Promise.all([
        publicBookingApi.getDoctors(),
        publicBookingApi.getLocations(),
      ]);
      
      setDoctors(doctorsRes.data || []);
      setLocations(locationsRes.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      enqueueSnackbar('Failed to load form data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!formData.doctorId || !formData.appointmentDate) return;

    try {
      setLoadingSlots(true);
      
      const dateStr = formData.appointmentDate.toISOString().split('T')[0];
      const slotsRes = await publicBookingApi.getAvailableSlots(formData.doctorId, dateStr);
      
      setAvailableSlots(slotsRes.data || []);
    } catch (error) {
      console.error('Error loading available slots:', error);
      enqueueSnackbar('Failed to load available slots', { variant: 'error' });
    } finally {
      setLoadingSlots(false);
    }
  };

  // Validation functions
  const validateStep = (step: number): boolean => {
    const newErrors: ValidationErrors = {};

    if (step === 0) {
      // Patient Details validation
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
        newErrors.phone = 'Please enter a valid phone number (10-15 digits)';
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.age.trim()) {
        newErrors.age = 'Age is required';
      } else if (isNaN(Number(formData.age)) || Number(formData.age) <= 0) {
        newErrors.age = 'Please enter a valid age';
      }
      if (!formData.gender) {
        newErrors.gender = 'Gender is required';
      }
    }

    if (step === 1) {
      // Appointment Details validation
      if (!formData.doctorId) {
        newErrors.doctorId = 'Please select a doctor';
      }
      if (!formData.locationId) {
        newErrors.locationId = 'Please select a location';
      }
      if (!formData.appointmentDate) {
        newErrors.appointmentDate = 'Please select an appointment date';
      } else {
        // Check if the selected date is in the past
        const selectedDate = new Date(formData.appointmentDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
          newErrors.appointmentDate = 'Cannot book appointments for past dates';
        }
      }
      if (!formData.selectedSlot) {
        newErrors.selectedSlot = 'Please select an available time slot';
      }
      if (!formData.reason.trim()) {
        newErrors.reason = 'Reason for visit is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof BookingFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSelectChange = (field: keyof BookingFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user makes selection
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateChange = (field: keyof BookingFormData) => (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: date,
    }));
    // Clear error when user selects date
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;

    try {
      setSubmitting(true);

      // Prepare patient data for registration
      const patientData = {
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        phoneNumber: formData.phone,
        email: formData.email || undefined,
        age: parseInt(formData.age),
        gender: formData.gender,
      };

      // Get selected slot details
      const selectedSlot = availableSlots.find(slot => slot.id === formData.selectedSlot);
      if (!selectedSlot) {
        throw new Error('Selected time slot is no longer available');
      }

      // Prepare appointment data - Note: patientId will be handled by the backend
      const appointmentData = {
        patientId: '', // Temporary placeholder - backend will replace this
        doctorId: formData.doctorId,
        locationId: formData.locationId,
        date: formData.appointmentDate?.toISOString().split('T')[0] || '',
        time: selectedSlot.startTime,
        duration: 15, // Default 15 minutes - can be calculated from slot
        type: 'CONSULTATION',
        status: 'SCHEDULED',
        priority: 'MEDIUM',
        reason: formData.reason,
        notes: formData.notes || undefined,
        isWalkIn: false,
      };

      const response = await publicBookingApi.bookAppointment({
        patientData,
        appointmentData
      });
      
      if (response) {
        enqueueSnackbar('Appointment booked successfully!', { variant: 'success' });
        setActiveStep(2); // Move to confirmation step
      } else {
        throw new Error('Failed to book appointment');
      }
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to book appointment. Please try again.';
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderPatientDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Patient Information
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please provide your personal details for the appointment booking.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="First Name"
          value={formData.firstName}
          onChange={handleInputChange('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={formData.lastName}
          onChange={handleInputChange('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone}
          onChange={handleInputChange('phone')}
          error={!!errors.phone}
          helperText={errors.phone}
          required
          placeholder="e.g., +1234567890"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={handleInputChange('email')}
          error={!!errors.email}
          helperText={errors.email || 'Optional, but recommended for confirmations'}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Age"
          type="number"
          value={formData.age}
          onChange={handleInputChange('age')}
          error={!!errors.age}
          helperText={errors.age}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.gender} required>
          <InputLabel>Gender</InputLabel>
          <Select
            value={formData.gender}
            onChange={handleSelectChange('gender')}
            label="Gender"
          >
            <MenuItem value="MALE">Male</MenuItem>
            <MenuItem value="FEMALE">Female</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </Select>
          {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderAppointmentDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Appointment Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select your preferred doctor, location, and time for the appointment.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.doctorId} required>
          <InputLabel>Doctor</InputLabel>
          <Select
            value={formData.doctorId}
            onChange={handleSelectChange('doctorId')}
            label="Doctor"
          >
            {doctors.map((doctor) => (
              <MenuItem key={doctor.id} value={doctor.id}>
                Dr. {doctor.firstName} {doctor.lastName}
                {doctor.department && ` - ${doctor.department.name}`}
              </MenuItem>
            ))}
          </Select>
          {errors.doctorId && <FormHelperText>{errors.doctorId}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={!!errors.locationId} required>
          <InputLabel>Location</InputLabel>
          <Select
            value={formData.locationId}
            onChange={handleSelectChange('locationId')}
            label="Location"
          >
            {locations.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </Select>
          {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Appointment Date"
            value={formData.appointmentDate}
            onChange={handleDateChange('appointmentDate')}
            minDate={new Date()} // Block past dates
            shouldDisableDate={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.appointmentDate,
                helperText: errors.appointmentDate,
                required: true,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth error={!!errors.selectedSlot} required>
          <InputLabel>Available Time Slots</InputLabel>
          <Select
            value={formData.selectedSlot}
            onChange={handleSelectChange('selectedSlot')}
            label="Available Time Slots"
            disabled={!formData.doctorId || !formData.appointmentDate || loadingSlots}
          >
            {loadingSlots ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading available slots...
              </MenuItem>
            ) : availableSlots.length === 0 ? (
              <MenuItem disabled>
                {formData.doctorId && formData.appointmentDate 
                  ? 'No available slots for selected date' 
                  : 'Please select doctor and date first'}
              </MenuItem>
            ) : (
              availableSlots.map((slot) => (
                <MenuItem key={slot.id} value={slot.id}>
                  {slot.startTime} - {slot.endTime}
                </MenuItem>
              ))
            )}
          </Select>
          {errors.selectedSlot && <FormHelperText>{errors.selectedSlot}</FormHelperText>}
          {!errors.selectedSlot && (formData.doctorId && formData.appointmentDate) && (
            <FormHelperText>
              {loadingSlots ? 'Loading...' : `${availableSlots.length} slots available`}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Reason for Visit"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          error={!!errors.reason}
          helperText={errors.reason}
          required
          multiline
          rows={2}
          placeholder="Brief description of your health concern or reason for the visit"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Additional Notes"
          value={formData.notes}
          onChange={handleInputChange('notes')}
          multiline
          rows={3}
          placeholder="Any additional information you'd like the doctor to know (optional)"
        />
      </Grid>
    </Grid>
  );

  const renderConfirmationStep = () => {
    const selectedDoctor = doctors.find(d => d.id === formData.doctorId);
    const selectedLocation = locations.find(l => l.id === formData.locationId);

    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Appointment Booked Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your appointment has been scheduled. You will receive a confirmation shortly.
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Appointment Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2">Patient</Typography>
                  <Typography variant="body2">
                    {formData.firstName} {formData.lastName}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2">Phone</Typography>
                  <Typography variant="body2">{formData.phone}</Typography>
                </Box>
              </Box>
            </Grid>

            {formData.email && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Email sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="subtitle2">Email</Typography>
                    <Typography variant="body2">{formData.email}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ mr: 1, bgcolor: 'primary.main', width: 24, height: 24 }}>
                  Dr
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">Doctor</Typography>
                  <Typography variant="body2">
                    {selectedDoctor ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2">Location</Typography>
                  <Typography variant="body2">
                    {selectedLocation?.name || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2">Date</Typography>
                  <Typography variant="body2">
                    {formData.appointmentDate?.toLocaleDateString() || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2">Time</Typography>
                  <Typography variant="body2">
                    {(() => {
                      const selectedSlot = availableSlots.find(slot => slot.id === formData.selectedSlot);
                      return selectedSlot ? `${selectedSlot.startTime} - ${selectedSlot.endTime}` : 'N/A';
                    })()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>What's Next?</strong><br />
            • You will receive a confirmation call/SMS shortly<br />
            • Please arrive 15 minutes before your appointment time<br />
            • Bring a valid ID and any relevant medical documents<br />
            • If you need to reschedule, please call us at least 24 hours in advance
          </Typography>
        </Alert>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ mr: 2 }}
          >
            Book Another Appointment
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.close()}
          >
            Close
          </Button>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Book an Appointment
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Schedule your visit with our healthcare professionals
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && renderPatientDetailsStep()}
          {activeStep === 1 && renderAppointmentDetailsStep()}
          {activeStep === 2 && renderConfirmationStep()}

          {activeStep < 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={activeStep === steps.length - 2 ? handleSubmit : handleNext}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {activeStep === steps.length - 2 ? 'Book Appointment' : 'Next'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default BookAppointment;
